import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/api';

const BAND = { green: 'text-emerald-300', amber: 'text-amber-300', red: 'text-rose-300' };
const ALIGN = { aligned: 'Aligned', under_confident: 'Under-confident', over_confident: 'Over-confident' };
const STATE_LABEL = { observed: 'Observed', insufficient_opportunity: 'No opportunity', no_opportunity_data: 'No opportunity data', not_observed: 'Not observed' };

function Score({ v, band }) {
  if (v == null) return <span className="text-slate-600">—</span>;
  return <span className={cn('font-semibold tabular-nums', BAND[band] || 'text-slate-200')}>{Math.round(v)}</span>;
}

// Per-learner (or per-cohort adapter row) health for the selected period.
export default function TransferLearnersTable({ organizationId, cohortId, period, capability }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    const p = new URLSearchParams();
    if (organizationId && organizationId !== '_all') p.set('organization_id', organizationId);
    if (cohortId && cohortId !== '_all') p.set('cohort_id', cohortId);
    if (period) p.set('period', period);
    if (capability && capability !== '_all') p.set('capability', capability);
    setData(null);
    api.get(`/transfer-health/learners?${p}`).then((r) => setData(r.data)).catch(() => setData({ learners: [] }));
  }, [organizationId, cohortId, period, capability]);

  if (!data) return <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></div>;
  const rows = data.learners.filter((l) => !q || `${l.name} ${l.subject} ${l.cohort_name}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2" data-testid="transfer-learners">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-slate-500">{rows.length} of {data.learners.length} subjects · sorted weakest Application first</p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter learners…" className="h-7 w-48 rounded-md bg-[#0b1220] border border-[#22304d] px-2 text-xs text-slate-200" data-testid="transfer-learners-filter" />
      </div>
      <div className="max-h-72 overflow-y-auto rounded-lg ring-1 ring-[#1f2a44]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#0b1220] text-[10px] uppercase tracking-wide text-slate-500">
            <tr><th className="text-left py-2 px-2">Learner</th><th className="text-left px-2">Cohort</th><th className="px-2">Conf</th><th className="px-2 text-left">Alignment</th><th className="px-2">App</th><th className="px-2">Rate</th><th className="px-2">Beh</th><th className="px-2 text-left">Opportunity</th><th className="px-2">Evidence</th></tr>
          </thead>
          <tbody className="divide-y divide-[#1f2a44]">
            {rows.map((l) => (
              <tr key={l.subject} data-testid={`transfer-learner-${l.subject}`}>
                <td className="py-1.5 px-2 text-slate-200">{l.name}{l.level === 'cohort' && <span className="ml-1 text-[9px] uppercase text-slate-500">cohort avg</span>}</td>
                <td className="px-2 text-slate-500">{l.cohort_name}</td>
                <td className="px-2 text-center"><Score v={l.confidence} band={l.bands.confidence} /></td>
                <td className={cn('px-2 whitespace-nowrap', l.alignment === 'over_confident' ? 'text-rose-300' : l.alignment === 'under_confident' ? 'text-amber-300' : 'text-slate-500')} data-testid={`transfer-learner-alignment-${l.subject}`}>{l.alignment ? `${ALIGN[l.alignment]} (${l.alignment_gap > 0 ? '+' : ''}${l.alignment_gap})` : '—'}</td>
                <td className="px-2 text-center"><Score v={l.application} band={l.bands.application} /></td>
                <td className="px-2 text-center text-slate-400 tabular-nums">{l.application_rate == null ? '—' : `${Math.round(l.application_rate)}%`}</td>
                <td className="px-2 text-center"><Score v={l.behavior} band={l.bands.behavior} /></td>
                <td className={cn('px-2', l.opportunity_state === 'insufficient_opportunity' ? 'text-slate-300' : 'text-slate-500')}>{STATE_LABEL[l.opportunity_state]}</td>
                <td className="px-2 text-center text-slate-400">{Object.values(l.evidence_confidence).map((e) => e.label?.[0] || '·').join(' ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
